import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { BillingCostReport, BillingInterface, PlanInterface, PriceType } from 'voxr-shared';
import { UserService } from './user.service';
import Stripe from 'stripe';

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private http = inject(HttpClient);
  private userService = inject(UserService);
  static PriceTypes: PriceType[] = ['plan', 'minutes', 'workspace', 'number', 'campaign', 'member', 'crm', 'knowledge'];

  constructor() {
    this.userService.user$.subscribe(async (user: any) => {
      if (!user) this._plans$ = undefined;
    });
  }

  private _plans$?: Promise<PlanInterface[]>;
  plans() {
    if (!this._plans$)
      this._plans$ = firstValueFrom(this.http.get<PlanInterface[]>(`${environment.app.serviceUrl}/billing/plans`));
    return this._plans$;
  }
  planCache: { [key: string]: Promise<PlanInterface> } = {};
  async getPlan(planId: string) {
    const plans = await this.plans();
    const plan = plans.find(_ => _._id!.toString() == planId) || this.planCache[planId];
    if (plan) return plan;

    return this.planCache[planId] || (this.planCache[planId] = firstValueFrom(this.http.get<PlanInterface>(`${environment.app.serviceUrl}/billing/plan/${planId}`)));
  }

  getMasterPlans() {
    return firstValueFrom(this.http.get<PlanInterface[]>(`${environment.app.serviceUrl}/billing/plans`, { params: { master: 'true' } }));
  }

  async checkout(planIdOrIsMaster?: string | true, addCrm?: boolean) {
    const result = await firstValueFrom(this.http.get<any>(`${environment.app.serviceUrl}/billing/checkout/${planIdOrIsMaster || ''}` + (addCrm ? '?crm=true' : '')));
    if (result?.url) location.href = result.url;
  }

  async subscribe(planId: string) {
    const result = await firstValueFrom(this.http.post<any>(`${environment.app.serviceUrl}/billing/subscribe`, { planId }));
    if (result?.url) location.href = result.url;
    else location.reload();
  }

  async connect() {
    const result = await firstValueFrom(this.http.get<any>(`${environment.app.serviceUrl}/billing/connect`));
    if (result?.url) location.href = result.url;
  }

  async fetchAccount() {
    return await firstValueFrom(this.http.get<any>(`${environment.app.serviceUrl}/billing/stripe-account`));
  }

  async stripeLogin() {
    return await firstValueFrom(this.http.get<{ url: string }>(`${environment.app.serviceUrl}/billing/stripe-login`));
  }

  async onboard(step: 'workspace' | 'crm' | 'telephony' | 'campaign') {
    return await firstValueFrom(this.http.post<{ step: 'workspace' | 'crm' | 'telephony', inprogress?: boolean, entity?: any }>(`${environment.app.serviceUrl}/billing/onboard/${step}`, null));
  }

  async autoOnboard() {
    return await firstValueFrom(this.http.post<any>(`${environment.app.serviceUrl}/billing/auto-onboard`, null));
  }

  async getPaymentMethods(billingId: string) {
    return firstValueFrom(this.http.get<BillingInterface>(`${environment.app.serviceUrl}/billing/stripe/payment-methods/${billingId}`));
  }

  async addPaymentMethod(billingId: string) {
    return firstValueFrom(this.http.get<Stripe.Checkout.Session>(`${environment.app.serviceUrl}/billing/stripe/add-payment-method/${billingId}`));
  }

  async setDefaultPaymentMethod(billingId: string, paymentMethodId: string) {
    return firstValueFrom(this.http.post<BillingInterface>(`${environment.app.serviceUrl}/billing/stripe/default-payment-method`, { billingId, paymentMethodId }));
  }

  async getInvoices() {
    return firstValueFrom(this.http.get<Stripe.Invoice[]>(`${environment.app.serviceUrl}/billing/invoices`));
  }

  async getInvoiceReport(invoiceId: string | 'upcoming') {
    return firstValueFrom(this.http.get<BillingCostReport>(`${environment.app.serviceUrl}/billing/invoice/${invoiceId}/report`));
  }
}
